import React, { useState } from 'react';

const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: 'Aa' },
  { value: 'textarea', label: 'Multi-line Text', icon: '¶' },
  { value: 'number', label: 'Number', icon: '#' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'select', label: 'Dropdown', icon: '▾' },
  { value: 'checkbox', label: 'Checkbox', icon: '☑' },
  { value: 'radio', label: 'Radio', icon: '◉' },
  { value: 'section', label: 'Section', icon: '─' },
];

const genId = () => 'f_' + Math.random().toString(36).substring(2, 9);

const FormBuilder = ({ template, isNew, folders, onSave, onCancel }) => {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [folderId, setFolderId] = useState(template?.folder_id || '');
  const [fields, setFields] = useState(template?.fields || []);
  const [dragIdx, setDragIdx] = useState(null);
  const [editingFieldIdx, setEditingFieldIdx] = useState(null);

  const addField = (type) => {
    const newField = { id: genId(), type, label: '', required: false };
    if (type === 'select' || type === 'radio') newField.options = ['Option 1', 'Option 2'];
    if (type === 'section') newField.label = 'New Section';
    setFields([...fields, newField]);
    setEditingFieldIdx(fields.length);
  };

  const updateField = (idx, updates) => {
    setFields(prev => prev.map((f, i) => i === idx ? { ...f, ...updates } : f));
  };

  const removeField = (idx) => {
    setFields(prev => prev.filter((_, i) => i !== idx));
    if (editingFieldIdx === idx) setEditingFieldIdx(null);
  };

  const moveField = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const next = [...fields];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setFields(next);
  };

  const duplicateField = (idx) => {
    const dup = { ...fields[idx], id: genId() };
    const next = [...fields];
    next.splice(idx + 1, 0, dup);
    setFields(next);
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: description.trim() || null, folder_id: folderId || null, fields });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px',
        borderBottom: '1px solid #e5e7eb', backgroundColor: '#fff',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onCancel} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#6b7280' }}>←</button>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
            {isNew ? 'Create Template' : `Edit: ${template?.name}`}
          </h3>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCancel} style={{ padding: '7px 16px', border: '1px solid #d1d5db', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            style={{ padding: '7px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: name.trim() ? 1 : 0.5 }}
          >
            {isNew ? 'Create Template' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left: Field Palette */}
        <div style={{
          width: 180, minWidth: 180, borderRight: '1px solid #e5e7eb', padding: 12,
          backgroundColor: '#fafbfc', overflowY: 'auto',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 8, letterSpacing: 0.5 }}>Add Fields</div>
          {FIELD_TYPES.map(ft => (
            <button
              key={ft.value}
              onClick={() => addField(ft.value)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px',
                border: '1px solid #e5e7eb', borderRadius: 6, backgroundColor: '#fff', cursor: 'pointer',
                fontSize: 13, color: '#374151', marginBottom: 6, textAlign: 'left',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.borderColor = '#2563eb'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
            >
              <span style={{ width: 22, textAlign: 'center', fontSize: 14, color: '#6b7280' }}>{ft.icon}</span>
              {ft.label}
            </button>
          ))}
        </div>

        {/* Center: Form Preview */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24, backgroundColor: '#f5f7fa' }}>
          <div style={{
            maxWidth: 640, margin: '0 auto', backgroundColor: '#fff', borderRadius: 8,
            border: '1px solid #e5e7eb', padding: 24,
          }}>
            {/* Template meta */}
            <div style={{ marginBottom: 24 }}>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Template Name *"
                style={{
                  width: '100%', padding: '10px 12px', fontSize: 18, fontWeight: 700, border: '1px solid #e5e7eb',
                  borderRadius: 6, outline: 'none', boxSizing: 'border-box',
                }}
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optional)"
                rows={2}
                style={{
                  width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid #e5e7eb',
                  borderRadius: 6, marginTop: 8, outline: 'none', resize: 'vertical', boxSizing: 'border-box',
                }}
              />
              {isNew && folders && folders.length > 0 && (
                <select
                  value={folderId}
                  onChange={(e) => setFolderId(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, marginTop: 8 }}
                >
                  <option value="">No folder (ungrouped)</option>
                  {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              )}
            </div>

            {/* Fields */}
            {fields.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: 40, border: '2px dashed #d1d5db', borderRadius: 8,
                color: '#9ca3af', fontSize: 14,
              }}>
                Add fields from the left panel to design your form
              </div>
            ) : (
              fields.map((field, idx) => (
                <div
                  key={field.id}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={() => { if (dragIdx !== null) { moveField(dragIdx, idx); setDragIdx(null); } }}
                  onDragEnd={() => setDragIdx(null)}
                  onClick={() => setEditingFieldIdx(idx)}
                  style={{
                    padding: 12, marginBottom: 8, border: editingFieldIdx === idx ? '2px solid #2563eb' : '1px solid #e5e7eb',
                    borderRadius: 6, cursor: 'grab', backgroundColor: dragIdx === idx ? '#f0f9ff' : '#fff',
                    position: 'relative',
                  }}
                >
                  {/* Drag handle */}
                  <div style={{
                    position: 'absolute', left: -4, top: '50%', transform: 'translateY(-50%)',
                    color: '#d1d5db', fontSize: 16, cursor: 'grab', padding: '0 4px',
                  }}>⋮⋮</div>

                  {field.type === 'section' ? (
                    <div style={{
                      fontSize: 14, fontWeight: 700, color: '#374151', borderBottom: '2px solid #e5e7eb',
                      paddingBottom: 6, paddingLeft: 16,
                    }}>
                      {field.label || 'Section Header'}
                    </div>
                  ) : (
                    <div style={{ paddingLeft: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>
                          {FIELD_TYPES.find(t => t.value === field.type)?.icon}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                          {field.label || 'Untitled Field'}
                          {field.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
                        </span>
                      </div>
                      <FieldPreview field={field} />
                    </div>
                  )}

                  {/* Field actions */}
                  <div style={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 4 }}>
                    <button onClick={(e) => { e.stopPropagation(); duplicateField(idx); }}
                      title="Duplicate" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: '#9ca3af', padding: 2 }}>📋</button>
                    <button onClick={(e) => { e.stopPropagation(); removeField(idx); }}
                      title="Remove" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: '#ef4444', padding: 2 }}>✕</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Field Properties */}
        {editingFieldIdx !== null && fields[editingFieldIdx] && (
          <div style={{
            width: 260, minWidth: 260, borderLeft: '1px solid #e5e7eb', padding: 16,
            backgroundColor: '#fafbfc', overflowY: 'auto',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 12, letterSpacing: 0.5 }}>Field Properties</div>
            <FieldEditor
              field={fields[editingFieldIdx]}
              onChange={(updates) => updateField(editingFieldIdx, updates)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const FieldPreview = ({ field }) => {
  const inputStyle = {
    width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 4,
    fontSize: 13, backgroundColor: '#f9fafb', boxSizing: 'border-box',
  };

  switch (field.type) {
    case 'text':
      return <input type="text" disabled placeholder="Text input" style={inputStyle} />;
    case 'textarea':
      return <textarea disabled placeholder="Multi-line text" rows={2} style={{ ...inputStyle, resize: 'none' }} />;
    case 'number':
      return <input type="number" disabled placeholder="0" style={{ ...inputStyle, width: 120 }} />;
    case 'date':
      return <input type="date" disabled style={{ ...inputStyle, width: 160 }} />;
    case 'select':
      return (
        <select disabled style={{ ...inputStyle, width: 200 }}>
          <option>Select...</option>
          {(field.options || []).map((o, i) => <option key={i}>{o}</option>)}
        </select>
      );
    case 'checkbox':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b7280' }}>
          <input type="checkbox" disabled /> {field.label || 'Checkbox'}
        </label>
      );
    case 'radio':
      return (
        <div style={{ display: 'flex', gap: 12 }}>
          {(field.options || []).map((o, i) => (
            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#6b7280' }}>
              <input type="radio" disabled name={field.id} /> {o}
            </label>
          ))}
        </div>
      );
    default:
      return null;
  }
};

const FieldEditor = ({ field, onChange }) => {
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 12 };
  const inputStyle = {
    display: 'block', width: '100%', padding: '6px 8px', border: '1px solid #d1d5db',
    borderRadius: 4, fontSize: 13, marginTop: 4, boxSizing: 'border-box',
  };

  return (
    <div>
      <label style={labelStyle}>
        Label
        <input type="text" value={field.label} onChange={(e) => onChange({ label: e.target.value })} style={inputStyle} />
      </label>

      <label style={labelStyle}>
        Type
        <select value={field.type} onChange={(e) => onChange({ type: e.target.value })} style={inputStyle}>
          {FIELD_TYPES.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
        </select>
      </label>

      {field.type !== 'section' && (
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={field.required || false} onChange={(e) => onChange({ required: e.target.checked })} />
          Required
        </label>
      )}

      {(field.type === 'select' || field.type === 'radio') && (
        <div style={labelStyle}>
          Options
          {(field.options || []).map((opt, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, marginTop: i === 0 ? 4 : 0 }}>
              <input
                type="text"
                value={opt}
                onChange={(e) => {
                  const newOpts = [...(field.options || [])];
                  newOpts[i] = e.target.value;
                  onChange({ options: newOpts });
                }}
                style={{ ...inputStyle, margin: 0, flex: 1 }}
              />
              <button
                onClick={() => onChange({ options: (field.options || []).filter((_, j) => j !== i) })}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14, padding: '0 4px' }}
              >✕</button>
            </div>
          ))}
          <button
            onClick={() => onChange({ options: [...(field.options || []), `Option ${(field.options || []).length + 1}`] })}
            style={{
              marginTop: 4, padding: '4px 10px', fontSize: 12, border: '1px dashed #d1d5db',
              borderRadius: 4, backgroundColor: 'transparent', color: '#6b7280', cursor: 'pointer',
            }}
          >
            + Add option
          </button>
        </div>
      )}

      {field.type === 'text' && (
        <label style={labelStyle}>
          Placeholder
          <input type="text" value={field.placeholder || ''} onChange={(e) => onChange({ placeholder: e.target.value })} style={inputStyle} />
        </label>
      )}
    </div>
  );
};

export default FormBuilder;
